import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Ajustes } from '../types'
import { AJUSTES_POR_DEFECTO, type Adapter, type Snapshot, type Tabla } from './adapter'

const URL = import.meta.env.VITE_SUPABASE_URL as string | undefined
const KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

export const supabaseConfigurado = Boolean(URL && KEY && URL.startsWith('http'))

// NovaBerry no usa Supabase Auth: el acceso a los datos lo da la clave
// "anon" sola, sin sesión de ningún tipo (ver supabase/schema.sql). Se
// desactiva la persistencia de sesión porque nunca hay una que guardar.
export const supabase: SupabaseClient | null = supabaseConfigurado
  ? createClient(URL!, KEY!, { auth: { persistSession: false, autoRefreshToken: false } })
  : null

/** Las columnas en Postgres se llaman igual que los campos de TypeScript
 *  (entre comillas dobles en el SQL). Así no hace falta una capa de
 *  traducción snake_case ↔ camelCase que solo agregaría lugares donde
 *  equivocarse. */
const TABLAS: Tabla[] = [
  'usuarios',
  'productos',
  'lotes',
  'movimientos',
  'clientes',
  'ventas',
  'devoluciones',
  'bitacora',
  'gastos',
]

/** Trae una tabla completa paginando: Supabase corta en 1000 filas. */
async function traerTodo(sb: SupabaseClient, tabla: string) {
  const filas: Record<string, unknown>[] = []
  const paso = 1000
  for (let desde = 0; ; desde += paso) {
    const { data, error } = await sb.from(tabla).select('*').range(desde, desde + paso - 1)
    if (error) throw error
    filas.push(...(data ?? []))
    if (!data || data.length < paso) break
  }
  return filas
}

export const supabaseAdapter: Adapter = {
  nombre: 'supabase',

  async disponible() {
    if (!supabase) return false
    const { error } = await supabase.from('ajustes').select('id').limit(1)
    return !error
  },

  async cargar(): Promise<Snapshot> {
    if (!supabase) throw new Error('Supabase no está configurado.')
    const resultados = await Promise.all(TABLAS.map((t) => traerTodo(supabase, t)))
    const { data: ajustes } = await supabase.from('ajustes').select('*').eq('id', 'ajustes').maybeSingle()

    const snapshot = Object.fromEntries(
      TABLAS.map((t, i) => [t, resultados[i]]),
    ) as unknown as Snapshot
    // Combinado con los valores por defecto, no solo reemplazado: si se
    // agrega un ajuste nuevo (como categoriasGasto) y la fila en la
    // nube todavía no pasó por la migración, no debe llegar `null` y
    // romper la pantalla — hereda el default hasta que se guarde.
    snapshot.ajustes = { ...AJUSTES_POR_DEFECTO, ...(ajustes as Ajustes | null) }
    return snapshot
  },

  async guardar(tabla, filas) {
    if (!supabase || !filas.length) return
    const { error } = await supabase.from(tabla).upsert(filas as never[], { onConflict: 'id' })
    if (error) throw error
  },

  async eliminar(tabla: Tabla, ids: string[]) {
    if (!supabase || !ids.length) return
    const { error } = await supabase.from(tabla).delete().in('id', ids)
    if (error) throw error
  },

  async guardarAjustes(ajustes: Ajustes) {
    if (!supabase) return
    const { error } = await supabase.from('ajustes').upsert(ajustes, { onConflict: 'id' })
    if (error) throw error
  },

  async reemplazarTodo(s: Snapshot) {
    if (!supabase) return
    // Se borra en orden inverso a las dependencias antes de reinsertar.
    // Cada operación revisa su error: sin esto, un rechazo de RLS (por
    // ejemplo, escribir sin sesión) pasaba desapercibido y la app creía
    // haber guardado datos que en realidad nunca llegaron a la base.
    for (const tabla of [...TABLAS].reverse()) {
      const { error } = await supabase.from(tabla).delete().neq('id', '')
      if (error) throw error
    }
    for (const tabla of TABLAS) {
      const filas = s[tabla] as unknown[]
      if (filas.length) {
        const { error } = await supabase.from(tabla).insert(filas as never[])
        if (error) throw error
      }
    }
    const { error } = await supabase.from('ajustes').upsert(s.ajustes, { onConflict: 'id' })
    if (error) throw error
  },
}
